import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCustomerParts } from '@/hooks/usePartsQuery';
import { useCartStore } from '@/stores/cartStore';
import PartCard from '@/components/inventory/PartCard';
import FilterBar from '@/components/inventory/FilterBar';
import type { CategoryId } from '@/data/categories';
import type { CurrencyCode } from '@/lib/currency';

export default function Inventory() {
  const { data: parts, isLoading, error } = useCustomerParts();
  const currency = useCartStore((s) => s.currency);
  const setCurrency = useCartStore((s) => s.setCurrency);
  const lineCount = useCartStore((s) => s.lines.length);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryId | 'all'>('all');

  const filtered = useMemo(() => {
    if (!parts) return [];
    return parts.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        const haystack = [
          p.title,
          p.sku,
          p.casting ?? '',
          p.description ?? '',
          ...p.fitment,
          String(p.year),
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [parts, category, search]);

  return (
    <div className="app">
      <div className="main">
        <div className="row between" style={{ marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className="eyebrow">Inventory · Authenticated Catalog</div>
            <h1 className="h-title" style={{ marginBottom: 6 }}>Browse the Catalog</h1>
            <p className="lead" style={{ fontSize: 16, margin: 0 }}>
              Every part below is photographed, casting-verified, and documented. Click any card
              for the full authentication record.
            </p>
          </div>
          <Link to="/quote" className="btn ghost sm" style={{ alignSelf: 'flex-start' }}>
            Quote ({lineCount}) →
          </Link>
        </div>

        <FilterBar
          search={search}
          onSearch={setSearch}
          category={category}
          onCategory={setCategory}
          currency={currency}
          onCurrency={(c: CurrencyCode) => setCurrency(c)}
          partCount={filtered.length}
        />

        {isLoading && (
          <div className="empty-state">
            <p>Loading inventory…</p>
          </div>
        )}

        {error && (
          <div className="auth-error">
            Failed to load inventory: {error instanceof Error ? error.message : String(error)}
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div
            className="empty-state"
            style={{ gridColumn: '1/-1', background: 'var(--bg-1)', border: '1px dashed var(--line-2)', padding: 60 }}
          >
            <div style={{ fontFamily: 'var(--display)', fontSize: 32, color: 'var(--text-dim)' }}>
              No parts match.
            </div>
            <p style={{ margin: '12px 0' }}>Try clearing your filters or searching a different term.</p>
            <button type="button" className="btn ghost sm" onClick={() => { setSearch(''); setCategory('all'); }}>
              ↺ Reset filters
            </button>
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <div className="parts-grid">
            {filtered.map((p) => (
              <PartCard key={p.id} part={p} currency={currency} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
